import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, getAccessToken, subscribeAccessToken } from "../lib/api.js";
import { logError } from "../lib/logger.js";

interface MeData {
  employeeId: string;
  /** For greeting the person by name; /me/profile already carries it. */
  firstName: string;
  /** The whole name, for places that sign or address the person formally. */
  fullName: string;
  role: string;
  country: string | null;
  sourceSystem: string | null;
  isUS: boolean;
  isADP: boolean;
  moduleAccess: Set<string>;
  gender: string | null;
  maritalStatus: string | null;
  status: string;
  isOnProbation: boolean;
  isOnActivePip: boolean;
  departmentWfhEligible: boolean | null;
  /**
   * Org-wide attendance approval switch. Defaults to true everywhere it is set:
   * it arrives with /me/module-access, after the first render, and starting it
   * false would flash the approval UI away and back on every load. This is the
   * opposite of moduleAccess, which deliberately starts closed.
   */
  attendanceApprovalsEnabled: boolean;
  // HRMS-0653: the profile card (sidebar avatar → ProfileCardDialog).
  email: string;
  employeeCode: string | null;
  designation: string | null;
  departmentName: string | null;
  officeName: string | null;
  officeCity: string | null;
  managerName: string | null;
  /** Presigned for 1 hour; refreshed before it expires. */
  photoUrl: string | null;
}

const MeContext = createContext<MeData | null>(null);
const SetPhotoContext = createContext<(url: string | null) => void>(() => {});

// /me/profile's photo URL is presigned for 1 hour; fetch a fresh one before then.
const PHOTO_REFRESH_MS = 50 * 60 * 1000;

// The sidebar stays empty until this lands. A 429 from the per-IP rate limit on page
// load, or a dropped connection, used to leave it empty for the rest of the session,
// so failed attempts are retried. The limit window is one minute: the last retry waits that long.
const MODULE_ACCESS_RETRY_DELAYS_MS = [2_000, 10_000, 30_000, 60_000];

function loadModuleAccess(setMe: (fn: (prev: MeData | null) => MeData | null) => void, attempt = 0) {
  api.get("/me/module-access")
    .then((r) => {
      const d = r.data as { modules: string[]; attendanceApprovalsEnabled?: boolean };
      setMe((prev) => (prev ? {
        ...prev,
        moduleAccess: new Set(d.modules),
        attendanceApprovalsEnabled: d.attendanceApprovalsEnabled ?? true,
      } : prev));
    })
    .catch((err) => {
      logError("MeContext.loadModuleAccess", err);
      const delay = MODULE_ACCESS_RETRY_DELAYS_MS[attempt];
      if (delay !== undefined) setTimeout(() => loadModuleAccess(setMe, attempt + 1), delay);
    });
}

export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeData | null>(null);

  useEffect(() => {
    api.get("/me/profile")
      .then((r) => {
        const d = r.data as {
          id: string;
          firstName?: string;
          lastName?: string;
          sourceSystem?: string | null;
          gender?: string | null;
          maritalStatus?: string | null;
          status?: string;
          isOnProbation?: boolean;
          isOnActivePip?: boolean;
          department?: { wfhEligible?: boolean; name?: string } | null;
          email?: string;
          employeeCode?: string | null;
          designation?: string | null;
          office?: { country?: string; name?: string; city?: string | null } | null;
          manager?: { firstName: string; lastName: string } | null;
          photoUrl?: string | null;
        };
        const country = d.office?.country ?? null;
        const sourceSystem = d.sourceSystem ?? null;
        const token = getAccessToken() ?? "";
        let role = "";
        try {
          role = String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["role"] ?? "");
        } catch (err) { logError("MeContext.parseRole", err); }
        setMe({
          employeeId: d.id,
          firstName: d.firstName ?? "",
          fullName: [d.firstName, d.lastName].filter(Boolean).join(" "),
          role,
          country,
          sourceSystem,
          isUS: (country ?? "").toUpperCase() === "US",
          isADP: sourceSystem === "ADP",
          moduleAccess: new Set(),
          gender: d.gender ?? null,
          maritalStatus: d.maritalStatus ?? null,
          status: d.status ?? "",
          isOnProbation: d.isOnProbation ?? false,
          isOnActivePip: d.isOnActivePip ?? false,
          departmentWfhEligible: d.department?.wfhEligible ?? null,
          attendanceApprovalsEnabled: true,
          email: d.email ?? "",
          employeeCode: d.employeeCode ?? null,
          designation: d.designation ?? null,
          departmentName: d.department?.name ?? null,
          officeName: d.office?.name ?? null,
          officeCity: d.office?.city ?? null,
          managerName: d.manager ? `${d.manager.firstName} ${d.manager.lastName}`.trim() : null,
          photoUrl: d.photoUrl ?? null,
        });
        loadModuleAccess(setMe);
      })
      .catch((err) => { logError("MeContext.loadMe", err); setMe(null); });
  }, []);

  useEffect(() => {
    return subscribeAccessToken((token) => {
      let role = "";
      try { role = String((JSON.parse(atob((token ?? "").split(".")[1] ?? "")) as Record<string, unknown>)["role"] ?? ""); } catch { /* no token yet */ }
      setMe((prev) => (prev ? { ...prev, role } : prev));
      loadModuleAccess(setMe);
    });
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => {
      api.get<{ photoUrl?: string | null }>("/me/profile")
        .then((r) => setMe((prev) => (prev ? { ...prev, photoUrl: r.data.photoUrl ?? null } : prev)))
        .catch((err) => logError("MeContext.refreshPhoto", err));
    }, PHOTO_REFRESH_MS);
    return () => window.clearInterval(t);
  }, []);

  const setPhotoUrl = useCallback((url: string | null) => {
    setMe((prev) => (prev ? { ...prev, photoUrl: url } : prev));
  }, []);

  return (
    <MeContext.Provider value={me}>
      <SetPhotoContext.Provider value={setPhotoUrl}>{children}</SetPhotoContext.Provider>
    </MeContext.Provider>
  );
}

/** Updates the signed-in user's photo everywhere right after it is saved or removed. */
export function useSetMyPhoto(): (url: string | null) => void {
  return useContext(SetPhotoContext);
}

export function useMe(): MeData {
  const ctx = useContext(MeContext);
  return ctx ?? {
    employeeId: "", firstName: "", fullName: "", role: "", country: null, sourceSystem: null, isUS: false, isADP: false, moduleAccess: new Set(),
    gender: null, maritalStatus: null, status: "", isOnProbation: false, isOnActivePip: false, departmentWfhEligible: null,
    attendanceApprovalsEnabled: true,
    email: "", employeeCode: null, designation: null, departmentName: null, officeName: null, officeCity: null,
    managerName: null, photoUrl: null,
  };
}

export function useMeLoading(): boolean {
  return useContext(MeContext) === null;
}
